import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { senhaForte } from "./CadastrarSenhaModal";
import { ArrowLeft, ChevronDown, Eye, EyeOff, Loader2, Pencil, Plus, ShieldAlert, Users, X } from "lucide-react";

export const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";
export const glassCard = {
  background: "rgba(40, 42, 50, 0.6)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(141, 144, 160, 0.5)", borderTop: "1px solid rgba(255, 255, 255, 0.1)",
};
export const inputClass = "w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff] focus:ring-1 focus:ring-[#b4c5ff] transition-all placeholder:text-[#c3c6d7]/70";
export const inputStyle = {
  background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)", color: "#e1e2ed", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
  colorScheme: "dark",
};
export const STATUS_STYLE = {
  Ativo: { bg: "rgba(126,240,160,0.12)", color: "#7ef0a0", border: "rgba(126,240,160,0.3)" },
  Inativo: { bg: "rgba(255,180,171,0.1)", color: "#ffb4ab", border: "rgba(255,180,171,0.3)" },
  Vencido: { bg: "rgba(244,162,97,0.12)", color: "#f4a261", border: "rgba(244,162,97,0.35)" },
};

// Beep curto de confirmação (Web Audio, sem dependência): liberado = dois tons ascendentes, negado = tom grave duplo
export function beepAcesso(liberado) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const freqs = liberado ? [880, 1318] : [311, 233];
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = liberado ? "sine" : "square";
      o.frequency.value = f;
      const t0 = ctx.currentTime + i * 0.13;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.13);
      o.connect(g).connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + 0.15);
    });
    setTimeout(() => ctx.close().catch(() => {}), 700);
  } catch { /* aparelho sem áudio */ }
}

export function fmtCpf(cpf) {
  const d = String(cpf || "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf || "—";
}

export function EditField({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>{label}</label>
      <input data-testid={testid} className={inputClass} style={inputStyle} {...props} />
    </div>
  );
}

export function SenhaField({ label, value, onChange, show, toggle, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>{label}</label>
      <div className="relative">
        <input type={show ? "text" : "password"} data-testid={testid} value={value}
          onChange={(e) => onChange(e.target.value)} placeholder="••••••••"
          className={`${inputClass} pr-14`} style={inputStyle} />
        <button type="button" data-testid={`${testid}-toggle`} onClick={toggle}
          aria-label={show ? "Ocultar senha" : "Mostrar senha"}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full active:bg-white/10"
          style={{ color: "#8d90a0", touchAction: "manipulation" }}>
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

export function AutorizacaoCard({ a, testid, podeRevogar = false, onRevogar = null }) {
  const ativa = a.esta_ativa === true;
  const motivos = a.motivos_inatividade?.length ? a.motivos_inatividade : [a.status_autorizacao || "Autorização inativa"];
  return (
    <div className="rounded-xl px-3 py-2.5" data-testid={testid}
      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold" style={{ color: "#e1e2ed" }}>{String(a.eql || "").toUpperCase()}</p>
          <p className="text-[10px] mt-0.5 truncate" style={{ color: "#8d90a0" }}>
            Autorizador: {a.autorizador_nome || "—"}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {ativa && (
            <span className="rounded-full px-2 py-1 text-[9px] font-bold"
              style={{ background: "rgba(126,240,160,0.12)", color: "#7ef0a0", border: "1px solid rgba(126,240,160,0.3)" }}
              data-testid={`${testid}-status`}>
              Ativa
            </span>
          )}
          {podeRevogar && (
            <button type="button" data-testid={`${testid}-revogar`} onClick={onRevogar}
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-bold active:scale-95 transition-transform"
              style={{ background: "rgba(255,180,171,0.1)", color: "#ffb4ab", border: "1px solid rgba(255,180,171,0.3)" }}>
              <X className="h-3 w-3" /> Revogar
            </button>
          )}
        </div>
      </div>
      {!ativa && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-motivos`}>
          {motivos.map((motivo, i) => (
            <span key={motivo} className="rounded-full px-2 py-1 text-[9px] font-bold"
              style={{ background: "rgba(255,180,171,0.1)", color: "#ffb4ab", border: "1px solid rgba(255,180,171,0.3)" }}
              data-testid={`${testid}-motivo-${i}`}>
              {motivo}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


export default function MobileFamiliares({ origem, eql, cpf, onClose }) {
  const [itens, setItens] = useState(null);
  const [selecionado, setSelecionado] = useState(null);
  const [avisoAtivacao, setAvisoAtivacao] = useState(null);
  const [form, setForm] = useState(null);
  const [senhaForm, setSenhaForm] = useState({ nova: "", confirma: "" });
  const [showN, setShowN] = useState(false);
  const [showC, setShowC] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [onlyAtivas, setOnlyAtivas] = useState(true);
  const [expandedCpf, setExpandedCpf] = useState(null);

  const qs = `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`;
  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/familiares?${qs}&only_ativas=${onlyAtivas}`);
      const d = await r.json();
      if (d.ok) setItens(d.itens);
      else toast.error("Não foi possível carregar os familiares.");
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem, eql, cpf, onlyAtivas]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirEdicao = (f) => {
    setSelecionado(f);
    setForm({
      nome: f.nome || "", rg: f.rg || "", data_nascimento: String(f.data_nascimento || "").slice(0, 10),
      email: f.email || "", celular: `${f.ddi_celular || ""}${f.celular || ""}`.replace(/(?!^\+)\D/g, ""),
      autoriza_visitas: !!f.autoriza_visitas,
    });
    setSenhaForm({ nova: "", confirma: "" });
    setErro("");
  };

  const selecionar = (f) => {
    if (f.status_ativo !== "Ativo") setAvisoAtivacao(f);
    else abrirEdicao(f);
  };

  const salvar = async () => {
    setErro("");
    if (!form.nome.trim()) { setErro("O nome não pode ficar em branco."); return; }
    if (senhaForm.nova || senhaForm.confirma) {
      if (!senhaForte(senhaForm.nova)) {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
        return;
      }
      if (senhaForm.nova !== senhaForm.confirma) {
        setErro("A confirmação não confere com a nova senha.");
        return;
      }
    }
    setSalvando(true);
    try {
      const payload = {
        origem, eql, cpf, familiar_cpf: selecionado.cpf,
        nome: form.nome, rg: form.rg, data_nascimento: form.data_nascimento,
        email: form.email, celular: form.celular,
      };
      if (form.autoriza_visitas !== !!selecionado.autoriza_visitas) {
        payload.autoriza_visitas = form.autoriza_visitas;
      }
      if (senhaForm.nova) payload.senha = senhaForm.nova;
      const r = await fetch(`${API}/public/familiares/atualizar`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(senhaForm.nova ? "Dados e senha do familiar atualizados." : "Dados do familiar atualizados.");
        setSelecionado(null);
        carregar();
      } else if (d.motivo === "senha_fraca") {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
      } else if (d.motivo === "dados_invalidos") {
        setErro("Dados inválidos. Verifique as informações preenchidas.");
      } else if (d.motivo === "familiar_nao_encontrado") {
        setErro("Familiar não localizado nesta unidade.");
      } else {
        setErro("Não foi possível salvar. Tente novamente.");
      }
    } catch {
      setErro("Falha de comunicação. Tente novamente.");
    } finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-familiares-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-familiares-voltar" aria-label="Voltar"
          onClick={() => (selecionado ? setSelecionado(null) : onClose())}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-familiares-titulo">
          {selecionado ? "Alterar Familiar" : "Familiares"}
        </h1>
        {!selecionado && (
          <button type="button" data-testid="appmobile-familiares-incluir" aria-label="Incluir familiar"
            className="inline-flex items-center gap-1 rounded-xl px-3 h-9 text-xs font-semibold"
            style={{ background: "rgba(37,99,235,0.2)", color: "#8ab4ff", border: "1px solid rgba(138,180,255,0.3)", touchAction: "manipulation" }}>
            <Plus className="h-4 w-4" /> Incluir
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        {!selecionado ? (
          !itens ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} /></div>
          ) : itens.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16" data-testid="appmobile-familiares-vazio">
              <Users className="h-8 w-8" style={{ color: "#8d90a0" }} />
              <p className="text-center text-xs" style={{ color: "#8d90a0" }}>Nenhum familiar cadastrado nesta unidade.</p>
            </div>
          ) : (
            <>
              <label className="mb-3 flex items-center gap-2.5 rounded-xl px-4 py-3 cursor-pointer"
                style={{ ...glassCard, color: "#c3c6d7" }} data-testid="appmobile-familiares-toggle-label">
                <input type="checkbox" data-testid="appmobile-familiares-toggle-todas" checked={!onlyAtivas}
                  onChange={(e) => { setOnlyAtivas(!e.target.checked); setItens(null); setExpandedCpf(null); }}
                  className="w-4 h-4 accent-[#2563EB]" />
                <span className="text-xs font-medium">Listar ativas e inativas</span>
              </label>
              <div className="rounded-xl overflow-hidden" style={glassCard} data-testid="appmobile-familiares-lista">
                <div className="grid grid-cols-[minmax(0,1fr)_64px_78px_72px] gap-2 px-3 py-2.5"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Nome</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Ativo</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Autorizador</span>
                  <span />
                </div>
                {itens.map((f) => {
                  const st = STATUS_STYLE[f.status_ativo] || STATUS_STYLE.Ativo;
                  const expanded = expandedCpf === f.cpf;
                  const autorizacoes = f.eqls_autorizadas || [];
                  return (
                    <div key={f.id || f.cpf} data-testid={`appmobile-familiar-item-${f.cpf}`}
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <div className="grid grid-cols-[minmax(0,1fr)_64px_78px_72px] gap-2 items-center px-3 py-3">
                        <span className="min-w-0 truncate text-xs font-semibold" style={{ color: "#e1e2ed" }}
                          data-testid={`appmobile-familiar-nome-${f.cpf}`}>{f.nome || "—"}</span>
                        <span className="text-[10px] font-bold text-center rounded-full px-1.5 py-1"
                          style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}
                          data-testid={`appmobile-familiar-ativo-${f.cpf}`}>{f.status_ativo}</span>
                        <span className="text-[11px] text-center" style={{ color: "#c3c6d7" }}
                          data-testid={`appmobile-familiar-autorizador-${f.cpf}`}>{f.autoriza_visitas ? "Sim" : "Não"}</span>
                        <div className="flex items-center justify-end gap-0.5">
                          <button type="button" data-testid={`appmobile-familiar-edit-${f.cpf}`} aria-label={`Editar ${f.nome || "familiar"}`}
                            onClick={() => selecionar(f)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg active:bg-white/10">
                            <Pencil className="h-4 w-4" style={{ color: "#8ab4ff" }} />
                          </button>
                          <button type="button" data-testid={`appmobile-familiar-toggle-${f.cpf}`}
                            aria-label={`${expanded ? "Recolher" : "Expandir"} autorizações de ${f.nome || "familiar"}`}
                            aria-expanded={expanded}
                            onClick={() => setExpandedCpf(expanded ? null : f.cpf)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg active:bg-white/10">
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                              style={{ color: "#8d90a0" }} />
                          </button>
                        </div>
                      </div>
                      {expanded && (
                        <div className="px-3 pb-3 pt-1" data-testid={`appmobile-familiar-autorizacoes-${f.cpf}`}>
                          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#b4c5ff" }}>
                            Autorizações
                          </p>
                          {autorizacoes.length ? (
                            <div className="flex flex-col gap-2">
                              {autorizacoes.map((a) => (
                                <AutorizacaoCard key={a.id || a.eql} a={a}
                                  testid={`appmobile-familiar-autorizacao-${f.cpf}-${a.id || a.eql}`} />
                              ))}
                            </div>
                          ) : (
                            <p className="text-[11px]" style={{ color: "#8d90a0" }}
                              data-testid={`appmobile-familiar-autorizacoes-vazio-${f.cpf}`}>
                              {onlyAtivas ? "Nenhuma autorização ativa." : "Nenhuma autorização."}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )
        ) : (
          <div className="flex flex-col gap-4" data-testid="appmobile-familiar-form">
            <div className="text-center mb-1">
              <p className="text-sm font-semibold" style={{ color: "#e1e2ed" }} data-testid="appmobile-familiar-local">
                {selecionado.local_acesso_descricao || "—"}
              </p>
              <p className="text-xs mt-0.5" style={{ color: "#8d90a0" }} data-testid="appmobile-familiar-identidade">
                {selecionado.eql} · Matrícula {selecionado.matricula || "—"} · CPF {fmtCpf(selecionado.cpf)}
              </p>
            </div>
            <EditField label="Nome" testid="appmobile-familiar-edit-nome" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
            <EditField label="RG" testid="appmobile-familiar-edit-rg" value={form.rg}
              onChange={(e) => setForm({ ...form, rg: e.target.value })} placeholder="Somente números" inputMode="numeric" />
            <EditField label="Nascimento" testid="appmobile-familiar-edit-nascimento" type="date" value={form.data_nascimento}
              onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} />
            <EditField label="E-mail" testid="appmobile-familiar-edit-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@dominio.com" />
            <EditField label="Whatsapp" testid="appmobile-familiar-edit-whatsapp" value={form.celular}
              onChange={(e) => setForm({ ...form, celular: e.target.value })} placeholder="+5511999999999" inputMode="tel" />

            <label className="flex items-start gap-2.5 text-xs rounded-xl px-4 py-3 cursor-pointer"
              style={{ ...glassCard, color: "#c3c6d7" }} data-testid="appmobile-familiar-autoriza-label">
              <input type="checkbox" data-testid="appmobile-familiar-autoriza" checked={form.autoriza_visitas}
                onChange={(e) => setForm({ ...form, autoriza_visitas: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#2563EB]" />
              <span>
                <span className="block font-semibold" style={{ color: "#e1e2ed" }}>Autoriza visitas</span>
                <span className="block text-[10px] mt-0.5" style={{ color: "#8d90a0" }}>
                  Define se o familiar pode receber visitas cadastradas pelo porteiro.
                </span>
              </span>
            </label>

            <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-familiar-senha-secao">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#b4c5ff" }}>Senha</p>
              <p className="text-[11px] mb-3" style={{ color: "#8d90a0" }}>
                Mantenha em branco para conservar a senha atual. Para alterar, preencha e confirme nos dois campos.
              </p>
              <div className="flex flex-col gap-3">
                <SenhaField label="Nova senha" value={senhaForm.nova} onChange={(x) => setSenhaForm({ ...senhaForm, nova: x })}
                  show={showN} toggle={() => setShowN(!showN)} testid="appmobile-familiar-edit-senha" />
                <SenhaField label="Confirmar nova senha" value={senhaForm.confirma} onChange={(x) => setSenhaForm({ ...senhaForm, confirma: x })}
                  show={showC} toggle={() => setShowC(!showC)} testid="appmobile-familiar-edit-senha2" />
              </div>
            </div>

            {erro && (
              <div data-testid="appmobile-familiar-erro"
                className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
                style={{ background: "rgba(255,180,171,0.08)", color: "#ffb4ab", border: "1px solid rgba(255,180,171,0.3)" }}>
                <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-familiar-cancelar" onClick={() => setSelecionado(null)} disabled={salvando}
                className="flex-1 py-3 rounded-xl text-sm font-medium active:scale-[0.98] transition-transform"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Cancelar
              </button>
              <button type="button" data-testid="appmobile-familiar-salvar" onClick={salvar} disabled={salvando}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
                style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
                {salvando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Salvar"}
              </button>
            </div>
          </div>
        )}
      </main>

      {avisoAtivacao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-familiar-ativacao-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
          <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5" style={glassCard}>
            <button type="button" data-testid="appmobile-familiar-ativacao-fechar" aria-label="Fechar"
              onClick={() => setAvisoAtivacao(null)}
              className="absolute right-3 top-3 w-9 h-9 flex items-center justify-center rounded-xl active:bg-white/10"
              style={{ color: "#8d90a0" }}>
              <X className="h-4 w-4" />
            </button>
            <ShieldAlert className="h-8 w-8 mx-auto mb-3" style={{ color: "#f4a261" }} />
            <h2 className="text-base font-bold text-center" style={{ color: "#e1e2ed" }}
              data-testid="appmobile-familiar-ativacao-titulo">Cadastro {avisoAtivacao.status_ativo}</h2>
            <p className="text-xs text-center mt-2 mb-5" style={{ color: "#c3c6d7" }}
              data-testid="appmobile-familiar-ativacao-texto">
              Solicite a ativação deste cadastro à administração. Você ainda pode alterar os dados agora.
            </p>
            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-familiar-ativacao-voltar" onClick={() => setAvisoAtivacao(null)}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Voltar
              </button>
              <button type="button" data-testid="appmobile-familiar-ativacao-continuar"
                onClick={() => { abrirEdicao(avisoAtivacao); setAvisoAtivacao(null); }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "#2563EB" }}>
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
